import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { temporal } from 'zundo'
import { type MouseEvent as TrackedMouseEvent } from '@/store/recording-store'
import { generateZoomSuggestions } from '@/lib/zoom-suggestions'

export type BackgroundType = 'solid' | 'gradient' | 'image'
export type PerspectiveType = 'none' | 'subtle-tilt' | 'hero-float' | 'tilt-left' | 'tilt-right' | 'isometric-left' | 'isometric-right' | 'dramatic-left' | 'dramatic-right'

export interface PerspectiveSettings {
    rotateX: number
    rotateY: number
    rotateZ: number
    scale: number
    perspective: number
}
export type AnimationType =
    | 'none'
    | 'pop'
    | 'fade'
    | 'slide-up'
    | 'slide-down'
    | 'slide-left'
    | 'slide-right'
    | 'zoom-in'
    | 'blur-in'
    | 'bounce-in'
    | 'bounce'
    | 'flip-x'
    | 'rotate-in'
    | 'back-in'
    | 'fade-out'
    | 'slide-out-up'
    | 'slide-out-down'
    | 'slide-out-left'
    | 'slide-out-right'
    | 'zoom-out'
    | 'blur-out'
    | 'bounce-out'
    | 'flip-x-out'
    | 'rotate-out'
    | 'typewriter'
export type EaseType = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut'

export interface ZoomRegion {
    id: string
    startTime: number
    endTime: number
    scale: number
    x: number
    y: number
    ease: EaseType
    mode?: 'focus' | 'pan'
}

// ... (PRESETS remain same)
export interface Clip {
    id: string
    start: number // Start time in source video
    end: number   // End time in source video
    offset: number // Start time on timeline
    status: 'visible' | 'deleted'
    speed?: number // Playback speed multiplier (default 1)
}

// Simple crop interface - stores as percentages
export interface VideoCrop {
    x: number      // percentage (0-100)
    y: number      // percentage (0-100)
    width: number  // percentage (0-100)
    height: number // percentage (0-100)
}


export interface ElementLayer {
    id: string
    type: 'arrow' | 'rectangle' | 'ellipse' | 'callout'
    startTime: number
    endTime: number
    color: string
    strokeWidth: number
    x: number // percentage (0-100)
    y: number // percentage (0-100)
    width: number
    height: number
    rotation: number // degrees
    opacity: number // 0-100
    // Callout specific
    colorText?: string
    fontFamily?: string
    fontSize?: number
    content?: string
    animationIn?: AnimationType
}

export interface TextLayer {
    id: string
    content: string
    startTime: number
    endTime: number
    fontSize: number
    color: string
    fontFamily: string
    fontWeight: string
    textAlign: 'left' | 'center' | 'right'
    x: number // percentage (0-100)
    y: number // percentage (0-100)
    opacity: number
    // Advanced Typography
    letterSpacing: number
    lineHeight: number
    textDecoration: 'none' | 'underline' | 'line-through'
    textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize'
    // Background
    backgroundColor?: string
    backgroundOpacity?: number
    backgroundPadding?: number
    backgroundBorderRadius?: number
    // Style
    textGradient?: string
    // Stroke
    strokeEnabled?: boolean
    strokeColor?: string
    strokeWidth?: number
    // Shadow
    textShadow?: string
    // Animation
    animationIn?: AnimationType
    animationOut?: AnimationType
}

export interface GradientPreset {
    name: string
    value: string
    category: 'animated' | 'linear' | 'texture' | 'holographic'
    animatedClass?: string
}

export const PRESET_BACKGROUNDS: GradientPreset[] = [
    // ⚡ Animated Mesh & Aurora Gradients (Feral UI Style)
    { name: 'Aurora Liquid', value: 'linear-gradient(135deg, #050515, #1e0533, #4c005c, #85007a, #300085, #050515)', category: 'animated', animatedClass: 'animate-aurora-flow' },
    { name: 'Cyberpunk Flow', value: 'linear-gradient(45deg, #0f0c29, #24243e, #ff007f, #00f2fe, #0f0c29)', category: 'animated', animatedClass: 'animate-bg-flow' },
    { name: 'Lava Lamp', value: 'linear-gradient(120deg, #12000b, #4a001e, #800000, #ff4500, #4a001e)', category: 'animated', animatedClass: 'animate-bg-flow' },
    { name: 'Neon Abyss', value: 'linear-gradient(135deg, #03001e, #7303c0, #ec38bc, #fdeff9, #03001e)', category: 'animated', animatedClass: 'animate-neon-pulse' },
    { name: 'Cosmic Nebula', value: 'linear-gradient(225deg, #09090e, #1a0826, #360940, #ff416c, #09090e)', category: 'animated', animatedClass: 'animate-aurora-flow' },
    { name: 'Ocean Currents', value: 'linear-gradient(45deg, #000428, #004e92, #00d2ff, #000428)', category: 'animated', animatedClass: 'animate-bg-flow' },
    { name: 'Golden Sunburst', value: 'linear-gradient(135deg, #1a0000, #4a1c00, #f857a6, #ff5858, #1a0000)', category: 'animated', animatedClass: 'animate-bg-flow' },
    { name: 'Emerald Matrix', value: 'linear-gradient(135deg, #001100, #003311, #00b09b, #96c93d, #001100)', category: 'animated', animatedClass: 'animate-bg-flow' },

    // 💎 Glass & Holographic / Metallic
    { name: 'Liquid Mercury', value: 'linear-gradient(135deg, #e0e0e0 0%, #ffffff 25%, #8e9eab 50%, #e0e0e0 75%, #eef2f3 100%)', category: 'holographic' },
    { name: 'Prism Rainbow', value: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 20%, #a1c4fd 40%, #c2e9fb 60%, #e0c3fc 80%, #8ec5fc 100%)', category: 'holographic' },
    { name: 'Dark Chrome', value: 'linear-gradient(135deg, #141E30 0%, #243B55 50%, #141E30 100%)', category: 'holographic' },
    { name: 'Frosted Pearl', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', category: 'holographic' },
    { name: 'Champagne Gold', value: 'linear-gradient(135deg, #bf953f 0%, #fcf6ba 25%, #b38728 50%, #fbf5b7 75%, #aa771c 100%)', category: 'holographic' },

    // 🌾 Texture & Grain
    { name: 'Vintage Slate', value: 'linear-gradient(135deg, #2c3e50 0%, #000000 100%)', category: 'texture' },
    { name: 'Matte Obsidian', value: 'linear-gradient(180deg, #111111 0%, #050505 100%)', category: 'texture' },
    { name: 'Dark Velvet', value: 'linear-gradient(135deg, #19091e 0%, #08020a 100%)', category: 'texture' },
    { name: 'Analog Sepia', value: 'linear-gradient(135deg, #3a2b25 0%, #1a100c 100%)', category: 'texture' },

    // 🌈 Linear Presets
    { name: 'Midnight', value: 'linear-gradient(to right, #0f2027, #203a43, #2c5364)', category: 'linear' },
    { name: 'Ultra Violet', value: 'linear-gradient(to right, #654ea3, #eaafc8)', category: 'linear' },
    { name: 'Sunset', value: 'linear-gradient(to right, #ff512f, #dd2476)', category: 'linear' },
    { name: 'Ocean', value: 'linear-gradient(to right, #2193b0, #6dd5ed)', category: 'linear' },
    { name: 'Emerald', value: 'linear-gradient(to right, #11998e, #38ef7d)', category: 'linear' },
    { name: 'Hyper', value: 'linear-gradient(to right, #6EE7B7, #3B82F6, #9333EA)', category: 'linear' },
    { name: 'Flare', value: 'linear-gradient(to right, #f12711, #f5af19)', category: 'linear' },
    { name: 'Turquoise', value: 'linear-gradient(to right, #134E5E, #71B280)', category: 'linear' },
    { name: 'Purpureus', value: 'linear-gradient(to right, #6a11cb, #2575fc)', category: 'linear' },
    { name: 'Rose', value: 'linear-gradient(to right, #e91e63, #fb8c00)', category: 'linear' },
    { name: 'Misty', value: 'linear-gradient(to right, #E0EAFC, #CFDEF3)', category: 'linear' },
    { name: 'Peach', value: 'linear-gradient(to right, #ED6663, #FFA372)', category: 'linear' },
    { name: 'Sky', value: 'linear-gradient(to right, #4facfe, #00f2fe)', category: 'linear' },
    { name: 'Lavender', value: 'linear-gradient(to right, #c471ed, #f64f59)', category: 'linear' },
    { name: 'Cloud', value: 'linear-gradient(to right, #fdfcfb, #e2d1c3)', category: 'linear' },
    { name: 'Cotton Candy', value: 'linear-gradient(to right, #ee9ca7, #ffdde1)', category: 'linear' },
    { name: 'Breeze', value: 'linear-gradient(to right, #89f7fe, #66a6ff)', category: 'linear' },
    { name: 'Lemon', value: 'linear-gradient(to right, #F7971E, #FFD200)', category: 'linear' },
    { name: 'Mint', value: 'linear-gradient(to right, #00b09b, #96c93d)', category: 'linear' },
    { name: 'Lilac', value: 'linear-gradient(to right, #7028e4, #e5b2ca)', category: 'linear' },
    { name: 'Royal', value: 'linear-gradient(to right, #141E30, #243B55)', category: 'linear' },
    { name: 'Cyber', value: 'linear-gradient(to right, #000428, #004e92)', category: 'linear' },
    { name: 'Void', value: 'linear-gradient(to right, #000000, #434343)', category: 'linear' },
    { name: 'Indigo', value: 'linear-gradient(to right, #3f51b5, #5c6bc0)', category: 'linear' },
    { name: 'Mars', value: 'linear-gradient(to right, #511031, #240b36)', category: 'linear' },
    { name: 'Vampire', value: 'linear-gradient(to right, #20002c, #cbb4d4)', category: 'linear' },
    { name: 'Steel', value: 'linear-gradient(to right, #1F1C2C, #928DAB)', category: 'linear' },
    { name: 'Forest', value: 'linear-gradient(to right, #134E5E, #71B280)', category: 'linear' },
    { name: 'Shadow', value: 'linear-gradient(to right, #2C3E50, #000000)', category: 'linear' },
    { name: 'Twilight', value: 'linear-gradient(to right, #09203f, #537895)', category: 'linear' },
    { name: 'Slate', value: 'linear-gradient(to right, #757F9A, #D7DDE8)', category: 'linear' },
    { name: 'Silver', value: 'linear-gradient(to right, #bdc3c7, #2c3e50)', category: 'linear' },
    { name: 'Gold', value: 'linear-gradient(to right, #D4AF37, #FFD700, #B8860B)', category: 'linear' },
    { name: 'Copper', value: 'linear-gradient(to right, #b79891, #94716b)', category: 'linear' },
    { name: 'Clean', value: 'linear-gradient(to right, #E8E8E8, #FFFFFF)', category: 'linear' },
    { name: 'Soft Gray', value: 'linear-gradient(to right, #f5f7fa, #c3cfe2)', category: 'linear' },
    { name: 'Dark Blue', value: 'linear-gradient(to right, #1e3c72, #2a5298)', category: 'linear' },
    { name: 'Rich Green', value: 'linear-gradient(to right, #11998e, #38ef7d)', category: 'linear' },
    { name: 'Deep Purple', value: 'linear-gradient(to right, #b92b27, #1565c0)', category: 'linear' },
]

export const PRESET_SHADOWS = [
    { name: 'None',   value: 'none' },
    { name: 'Soft',   value: '0 8px 24px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2)' },
    { name: 'Medium', value: '0 16px 40px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)' },
    { name: 'Large',  value: '0 28px 60px rgba(0,0,0,0.65), 0 8px 20px rgba(0,0,0,0.4)' },
    { name: 'Heavy',  value: '0 40px 80px rgba(0,0,0,0.85), 0 12px 32px rgba(0,0,0,0.5)' },
]

export const PERSPECTIVE_PRESETS: Record<PerspectiveType, PerspectiveSettings> = {
    'none':            { rotateX: 0,  rotateY:   0, rotateZ:   0, scale: 1.00, perspective: 1000 },
    // Cinematic — product hero shots used by Raycast, Linear, Vercel
    'subtle-tilt':     { rotateX: 5,  rotateY:  18, rotateZ:   0, scale: 0.90, perspective: 1200 },
    'hero-float':      { rotateX: 12, rotateY:   0, rotateZ:  -2, scale: 0.92, perspective: 900  },
    'tilt-left':       { rotateX: 8,  rotateY:  32, rotateZ:   0, scale: 0.78, perspective: 900  },
    'tilt-right':      { rotateX: 8,  rotateY: -32, rotateZ:   0, scale: 0.78, perspective: 900  },
    // Classic isometric
    'isometric-left':  { rotateX: 60, rotateY:   0, rotateZ:  45, scale: 0.80, perspective: 1000 },
    'isometric-right': { rotateX: 60, rotateY:   0, rotateZ: -45, scale: 0.80, perspective: 1000 },
    // Dramatic wide-angle perspective
    'dramatic-left':   { rotateX: 15, rotateY:  55, rotateZ:   0, scale: 0.70, perspective: 1000 },
    'dramatic-right':  { rotateX: 15, rotateY: -55, rotateZ:   0, scale: 0.70, perspective: 1000 },
}

export interface AudioLayer {
    id: string
    name: string
    type: 'recording' | 'music' | 'sfx'
    offset: number      // start position on timeline (seconds)
    duration: number    // playback duration (seconds)
    trimStart?: number  // how far into the source file to start (seconds), default 0
    volume: number      // 0.0–1.0
    muted: boolean
    fadeIn?: number     // fade-in duration in seconds (default 0)
    fadeOut?: number    // fade-out duration in seconds (default 0)
    pan?: number        // stereo pan -1 (L) to +1 (R), default 0
    url?: string        // object URL (music layers only; undefined for 'recording' type)
    waveform?: number[] // normalized 0–1 amplitude values for waveform visualization
    originalDuration?: number // the full, unclipped duration of the source file (seconds)
}

export interface CameraLayer {
    visible: boolean
    x: number      // percentage (0-100)
    y: number      // percentage (0-100)
    size: number   // percentage (0-100)
    mask: 'square' | 'rectangle' | 'circle'
    borderWidth: number
    borderColor: string
    borderRadius: number // corner radius for square/rectangle masks (in px)
    timeRange?: { start: number; end: number } // Timeline range (undefined = full duration)
}

export type CursorStyle = 'auto' | 'default' | 'dot' | 'crosshair' | 'pointer' | 'circle' | 'ring'
export type ClickEffectType = 'none' | 'ripple' | 'pulse' | 'ring'

export interface CursorSettings {
    visible: boolean
    style: CursorStyle
    size: number             // scale multiplier (0.5 - 3)
    color: string            // cursor color
    clickEffect: ClickEffectType
    clickEffectColor: string
    clickEffectSize: number  // 1-3
    smoothing: number        // 0-1, path interpolation smoothness
    highlightClicks: boolean
    // Effects
    spotlight: boolean       // Spotlight highlight effect
    // Spotlight-specific
    spotlightRadius: number  // 0.05–0.5, relative to min(width, height)
    spotlightOpacity: number // 0–1, darkness of the surrounding vignette
}

interface EditorState {
    // Appearance
    backgroundType: BackgroundType
    backgroundValue: string
    padding: number
    borderRadius: number
    shadow: string
    aspectRatio: string

    // Advanced Layout
    perspective: PerspectiveType
    perspectiveSettings: PerspectiveSettings
    clipContent: boolean
    borderWidth: number
    borderColor: string
    borderOpacity: number

    // Animation
    animationIn: AnimationType
    animationOut: AnimationType

    // Timeline Data
    zoomRegions: ZoomRegion[]
    clips: Clip[] // Video Clips (Splitting Engine)
    thumbnails: string[]
    setThumbnails: (thumbnails: string[]) => void
    currentTime: number
    // Background Effects
    backgroundBlur: number
    backgroundDim: number
    backgroundNoise: number // 0 to 1 noise opacity
    backgroundAttribution: {
        author: string
        authorUrl: string
        source: string
        sourceUrl: string
    } | null
    duration: number
    sourceDuration: number

    // Text Layers
    textLayers: TextLayer[]
    selectedTextLayerId: string | null

    // Element Layers
    elementLayers: ElementLayer[]
    selectedElementLayerId: string | null

    // Camera Layer
    cameraLayer: CameraLayer
    updateCameraLayer: (updates: Partial<CameraLayer>) => void

    // Cursor Layer
    cursorSettings: CursorSettings
    updateCursorSettings: (updates: Partial<CursorSettings>) => void

    // Actions
    setBackground: (type: BackgroundType, value: string, attribution?: EditorState['backgroundAttribution']) => void
    setBackgroundBlur: (val: number) => void
    setBackgroundDim: (val: number) => void
    setBackgroundNoise: (val: number) => void
    setPadding: (val: number) => void
    setBorderRadius: (val: number) => void
    setShadow: (val: string) => void
    setAspectRatio: (val: string) => void
    setPerspective: (val: PerspectiveType) => void
    updatePerspectiveSettings: (updates: Partial<PerspectiveSettings>) => void
    setClipContent: (val: boolean) => void
    setBorderWidth: (val: number) => void
    setBorderColor: (val: string) => void
    setBorderOpacity: (val: number) => void
    setAnimationIn: (val: AnimationType) => void
    setAnimationOut: (val: AnimationType) => void

    // Zoom Actions
    addZoomRegion: (region: ZoomRegion) => void
    updateZoomRegion: (id: string, updates: Partial<ZoomRegion>) => void
    removeZoomRegion: (id: string) => void

    // Clip Actions (Visual Editing)
    initClips: (duration: number, hasCamera?: boolean) => void
    splitClip: (atTime: number) => void
    deleteClip: (id: string) => void
    updateClip: (id: string, updates: Partial<Clip>) => void
    removeGap: (atTime: number) => void

    // Auto Zoom
    generateAutoZoom: (events: TrackedMouseEvent[], screenWidth: number, screenHeight: number) => void

    // Selection
    selectedClipId: string | null
    setSelectedClip: (id: string | null) => void
    selectedZoomRegionId: string | null
    setSelectedZoomRegion: (id: string | null) => void
    selectedCameraLayerSelected: boolean
    setSelectedCameraLayer: (selected: boolean) => void

    // Track Visibility
    trackVisibility: {
        video: boolean
        zoom: boolean
        text: boolean
        elements: boolean
        camera: boolean
        music: boolean
        sfx: boolean
    }
    toggleTrackVisibility: (track: keyof EditorState['trackVisibility']) => void

    // Duplication Actions
    duplicateClip: (id: string) => void
    duplicateTextLayer: (id: string) => void
    duplicateAudioLayer: (id: string) => void
    duplicateZoomRegion: (id: string) => void

    // Clipboard Actions
    clipboardItem: { type: 'clip' | 'text' | 'audio' | 'zoom'; data: any } | null
    copySelectedItem: () => void
    pasteClipboardItem: () => void

    // Playback
    setCurrentTime: (time: number) => void
    setDuration: (duration: number) => void
    isPlaying: boolean
    setIsPlaying: (isPlaying: boolean) => void

    // Crop
    videoCrop: VideoCrop | null
    setVideoCrop: (crop: VideoCrop | null) => void

    // Text Layer Actions
    addTextLayer: (layer: TextLayer) => void
    updateTextLayer: (id: string, updates: Partial<TextLayer>) => void
    removeTextLayer: (id: string) => void
    setSelectedTextLayer: (id: string | null) => void

    // Element Layer Actions
    duplicateElementLayer?: (id: string) => void
    addElementLayer: (layer: ElementLayer) => void
    updateElementLayer: (id: string, updates: Partial<ElementLayer>) => void
    removeElementLayer: (id: string) => void
    setSelectedElementLayer: (id: string | null) => void


    // Audio Layer Actions
    audioLayers: AudioLayer[]
    selectedAudioLayerId: string | null
    setSelectedAudioLayer: (id: string | null) => void
    addAudioLayer: (layer: Omit<AudioLayer, 'id'>) => void
    updateAudioLayer: (id: string, updates: Partial<AudioLayer>) => void
    removeAudioLayer: (id: string) => void

    // Recording Audio Controls
    recordingVolume: number
    recordingMuted: boolean
    setRecordingVolume: (vol: number) => void
    setRecordingMuted: (muted: boolean) => void

    reset: () => void
}

export const useEditorStore = create<EditorState>()(
    temporal(
        persist(
            (set) => ({
                backgroundType: 'gradient',
                backgroundValue: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                backgroundBlur: 0,
                backgroundDim: 0,
                backgroundNoise: 0,
                backgroundAttribution: null,
                padding: 48,
                borderRadius: 12,
                shadow: '0 16px 40px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)',
                aspectRatio: '16/9',
                perspective: 'none',
                perspectiveSettings: PERSPECTIVE_PRESETS['none'],
                clipContent: true,
                borderWidth: 7,
                borderColor: '#ffffff',
                borderOpacity: 0.05,
                animationIn: 'none',
                animationOut: 'none',
                zoomRegions: [],
                clips: [],
                thumbnails: [],
                currentTime: 0,
                duration: 0,
                sourceDuration: 0,
                isPlaying: true,
                selectedClipId: null,
                selectedTextLayerId: null,
                    selectedElementLayerId: null,
                selectedAudioLayerId: null,
                selectedZoomRegionId: null,
                selectedCameraLayerSelected: false,
                trackVisibility: {
                    video: true,
                    zoom: true,
                    text: true,
                    elements: true,
                    camera: true,
                    music: true,
                    sfx: true
                },
                clipboardItem: null,
                videoCrop: null,
                textLayers: [],
                elementLayers: [],
                audioLayers: [],
                recordingVolume: 1,
                recordingMuted: false,
                cameraLayer: {
                    visible: false,
                    x: 88,
                    y: 80,
                    size: 15,
                    mask: 'square',
                    borderWidth: 2,
                    borderColor: '#3b82f6',
                    borderRadius: 35
                },
                cursorSettings: {
                    visible: true,
                    style: 'circle',
                    size: 1.2,
                    color: '#ffffff',
                    clickEffect: 'ripple',
                    clickEffectColor: '#3b82f6',
                    clickEffectSize: 2,
                    smoothing: 0.7,
                    highlightClicks: true,
                    spotlight: false,
                    spotlightRadius: 0.22,
                    spotlightOpacity: 0.65,
                },

                reset: () => set({
                    backgroundType: 'gradient',
                    backgroundValue: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    backgroundBlur: 0,
                    backgroundDim: 0,
                    backgroundAttribution: null,
                    padding: 48,
                    borderRadius: 12,
                    shadow: '0 16px 40px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)',
                    aspectRatio: '16/9',
                    perspective: 'none',
                    perspectiveSettings: PERSPECTIVE_PRESETS['none'],
                    clipContent: true,
                    borderWidth: 7,
                    borderColor: '#ffffff',
                    borderOpacity: 0.05,
                    animationIn: 'none',
                    animationOut: 'none',
                    zoomRegions: [],
                    clips: [],
                    thumbnails: [],
                    currentTime: 0,
                    duration: 0,
                    sourceDuration: 0,
                    isPlaying: true,
                    selectedClipId: null,
                    selectedTextLayerId: null,
                    selectedElementLayerId: null,
                    selectedAudioLayerId: null,
                    selectedZoomRegionId: null,
                    selectedCameraLayerSelected: false,
                    trackVisibility: {
                        video: true,
                        zoom: true,
                        text: true,
                        elements: true,
                        camera: true,
                        music: true,
                        sfx: true
                    },
                    clipboardItem: null,
                    videoCrop: null,
                    textLayers: [],
                elementLayers: [],
                    audioLayers: [],
                    recordingVolume: 1,
                    recordingMuted: false,
                    cameraLayer: {
                        visible: false,
                        x: 88,
                        y: 80,
                        size: 15,
                        mask: 'square',
                        borderWidth: 2,
                        borderColor: '#3b82f6',
                        borderRadius: 35
                    },
                    cursorSettings: {
                        visible: true,
                        style: 'default',
                        size: 1.2,
                        color: '#ffffff',
                        clickEffect: 'ripple',
                        clickEffectColor: '#3b82f6',
                        clickEffectSize: 2,
                        smoothing: 0.7,
                        highlightClicks: true,
                        spotlight: false,
                        spotlightRadius: 0.22,
                        spotlightOpacity: 0.65,
                    },
                }),

                setRecordingVolume: (recordingVolume) => set({ recordingVolume }),
                setRecordingMuted: (recordingMuted) => set({ recordingMuted }),
                setSelectedClip: (selectedClipId) => set({
                    selectedClipId,
                    selectedTextLayerId: null,
                    selectedElementLayerId: null,
                    selectedAudioLayerId: null,
                    selectedZoomRegionId: null,
                    selectedCameraLayerSelected: false
                }),
                setSelectedElementLayer: (selectedElementLayerId) => set({
                    selectedElementLayerId,
                    selectedTextLayerId: null,
                    selectedClipId: null,
                    selectedAudioLayerId: null,
                    selectedZoomRegionId: null,
                    selectedCameraLayerSelected: false
                }),
                setSelectedTextLayer: (selectedTextLayerId) => set({
                    selectedTextLayerId,
                    selectedClipId: null,
                    selectedAudioLayerId: null,
                    selectedZoomRegionId: null,
                    selectedCameraLayerSelected: false
                }),
                setSelectedAudioLayer: (selectedAudioLayerId) => set({
                    selectedAudioLayerId,
                    selectedClipId: null,
                    selectedTextLayerId: null,
                    selectedElementLayerId: null,
                    selectedZoomRegionId: null,
                    selectedCameraLayerSelected: false
                }),
                setSelectedZoomRegion: (selectedZoomRegionId) => set({
                    selectedZoomRegionId,
                    selectedClipId: null,
                    selectedTextLayerId: null,
                    selectedElementLayerId: null,
                    selectedAudioLayerId: null,
                    selectedCameraLayerSelected: false
                }),
                setSelectedCameraLayer: (selectedCameraLayerSelected) => set({
                    selectedCameraLayerSelected,
                    selectedClipId: null,
                    selectedTextLayerId: null,
                    selectedElementLayerId: null,
                    selectedAudioLayerId: null,
                    selectedZoomRegionId: null
                }),
                toggleTrackVisibility: (track) => set((state) => ({
                    trackVisibility: {
                        ...state.trackVisibility,
                        [track]: !state.trackVisibility[track]
                    }
                })),

                duplicateClip: (id) => set((state) => {
                    const clip = state.clips.find(c => c.id === id)
                    if (!clip) return {}
                    const speed = clip.speed ?? 1
                    const clipDur = (clip.end - clip.start) / speed
                    const newClip: Clip = {
                        ...clip,
                        id: crypto.randomUUID(),
                        offset: clip.offset + clipDur
                    }
                    const updatedClips = state.clips.map(c => {
                        if (c.status === 'visible' && c.offset >= newClip.offset - 0.01) {
                            return { ...c, offset: c.offset + clipDur }
                        }
                        return c
                    })
                    updatedClips.push(newClip)
                    updatedClips.sort((a, b) => a.offset - b.offset)
                    return {
                        clips: updatedClips,
                        selectedClipId: newClip.id,
                        selectedTextLayerId: null,
                    selectedElementLayerId: null,
                        selectedAudioLayerId: null,
                        selectedZoomRegionId: null,
                        selectedCameraLayerSelected: false,
                        duration: Math.max(state.duration, newClip.offset + clipDur)
                    }
                }),

                duplicateTextLayer: (id) => set((state) => {
                    const layer = state.textLayers.find(l => l.id === id)
                    if (!layer) return {}
                    const dur = layer.endTime - layer.startTime
                    const newLayer: TextLayer = {
                        ...layer,
                        id: crypto.randomUUID(),
                        startTime: layer.endTime + 0.1,
                        endTime: layer.endTime + 0.1 + dur
                    }
                    return {
                        textLayers: [...state.textLayers, newLayer],
                        selectedTextLayerId: newLayer.id,
                        selectedClipId: null,
                        selectedAudioLayerId: null,
                        selectedZoomRegionId: null,
                        selectedCameraLayerSelected: false
                    }
                }),

                duplicateAudioLayer: (id) => set((state) => {
                    const layer = state.audioLayers.find(l => l.id === id)
                    if (!layer) return {}
                    const newLayer: AudioLayer = {
                        ...layer,
                        id: crypto.randomUUID(),
                        offset: layer.offset + layer.duration + 0.1
                    }
                    return {
                        audioLayers: [...state.audioLayers, newLayer],
                        selectedAudioLayerId: newLayer.id,
                        selectedClipId: null,
                        selectedTextLayerId: null,
                    selectedElementLayerId: null,
                        selectedZoomRegionId: null,
                        selectedCameraLayerSelected: false
                    }
                }),

                duplicateZoomRegion: (id) => set((state) => {
                    const region = state.zoomRegions.find(r => r.id === id)
                    if (!region) return {}
                    const dur = region.endTime - region.startTime
                    const newRegion: ZoomRegion = {
                        ...region,
                        id: crypto.randomUUID(),
                        startTime: region.endTime + 0.1,
                        endTime: region.endTime + 0.1 + dur
                    }
                    return {
                        zoomRegions: [...state.zoomRegions, newRegion],
                        selectedZoomRegionId: newRegion.id,
                        selectedClipId: null,
                        selectedTextLayerId: null,
                    selectedElementLayerId: null,
                        selectedAudioLayerId: null,
                        selectedCameraLayerSelected: false
                    }
                }),

                copySelectedItem: () => set((state) => {
                    if (state.selectedClipId) {
                        const clip = state.clips.find(c => c.id === state.selectedClipId)
                        if (clip) return { clipboardItem: { type: 'clip', data: clip } }
                    }
                    if (state.selectedTextLayerId) {
                        const layer = state.textLayers.find(l => l.id === state.selectedTextLayerId)
                        if (layer) return { clipboardItem: { type: 'text', data: layer } }
                    }
                    if (state.selectedAudioLayerId) {
                        const layer = state.audioLayers.find(l => l.id === state.selectedAudioLayerId)
                        if (layer) return { clipboardItem: { type: 'audio', data: layer } }
                    }
                    if (state.selectedZoomRegionId) {
                        const region = state.zoomRegions.find(r => r.id === state.selectedZoomRegionId)
                        if (region) return { clipboardItem: { type: 'zoom', data: region } }
                    }
                    return {}
                }),

                pasteClipboardItem: () => set((state) => {
                    const item = state.clipboardItem
                    if (!item) return {}
                    const time = state.currentTime

                    if (item.type === 'clip') {
                        const clip = item.data as Clip
                        const speed = clip.speed ?? 1
                        const clipDur = (clip.end - clip.start) / speed
                        const newClip: Clip = {
                            ...clip,
                            id: crypto.randomUUID(),
                            offset: time
                        }
                        const updatedClips = state.clips.map(c => {
                            if (c.status === 'visible' && c.offset >= time - 0.01) {
                                return { ...c, offset: c.offset + clipDur }
                            }
                            return c
                        })
                        updatedClips.push(newClip)
                        updatedClips.sort((a, b) => a.offset - b.offset)
                        return {
                            clips: updatedClips,
                            selectedClipId: newClip.id,
                            selectedTextLayerId: null,
                    selectedElementLayerId: null,
                            selectedAudioLayerId: null,
                            selectedZoomRegionId: null,
                            selectedCameraLayerSelected: false,
                            duration: Math.max(state.duration, time + clipDur)
                        }
                    }

                    if (item.type === 'text') {
                        const layer = item.data as TextLayer
                        const dur = layer.endTime - layer.startTime
                        const newLayer: TextLayer = {
                            ...layer,
                            id: crypto.randomUUID(),
                            startTime: time,
                            endTime: time + dur
                        }
                        return {
                            textLayers: [...state.textLayers, newLayer],
                            selectedTextLayerId: newLayer.id,
                            selectedClipId: null,
                            selectedAudioLayerId: null,
                            selectedZoomRegionId: null,
                            selectedCameraLayerSelected: false
                        }
                    }

                    if (item.type === 'audio') {
                        const layer = item.data as AudioLayer
                        const newLayer: AudioLayer = {
                            ...layer,
                            id: crypto.randomUUID(),
                            offset: time
                        }
                        return {
                            audioLayers: [...state.audioLayers, newLayer],
                            selectedAudioLayerId: newLayer.id,
                            selectedClipId: null,
                            selectedTextLayerId: null,
                    selectedElementLayerId: null,
                            selectedZoomRegionId: null,
                            selectedCameraLayerSelected: false
                        }
                    }

                    if (item.type === 'zoom') {
                        const region = item.data as ZoomRegion
                        const dur = region.endTime - region.startTime
                        const newRegion: ZoomRegion = {
                            ...region,
                            id: crypto.randomUUID(),
                            startTime: time,
                            endTime: time + dur
                        }
                        return {
                            zoomRegions: [...state.zoomRegions, newRegion],
                            selectedZoomRegionId: newRegion.id,
                            selectedClipId: null,
                            selectedTextLayerId: null,
                    selectedElementLayerId: null,
                            selectedAudioLayerId: null,
                            selectedCameraLayerSelected: false
                        }
                    }

                    return {}
                }),

                setThumbnails: (thumbnails) => set({ thumbnails }),
                setVideoCrop: (videoCrop) => set({ videoCrop }),

                setIsPlaying: (isPlaying) => set({ isPlaying }),
                setBackground: (type, value, attribution = null) => set({
                    backgroundType: type,
                    backgroundValue: value,
                    backgroundAttribution: attribution
                }),
                setBackgroundBlur: (backgroundBlur) => set({ backgroundBlur }),
                setBackgroundDim: (backgroundDim) => set({ backgroundDim }),
                setBackgroundNoise: (backgroundNoise) => set({ backgroundNoise }),
                setPadding: (padding) => set({ padding }),
                setBorderRadius: (borderRadius) => set({ borderRadius }),
                setShadow: (shadow) => set({ shadow }),
                setAspectRatio: (aspectRatio) => set({
                    aspectRatio,
                    videoCrop: null // Reset crop when ratio changes to prevent stretching
                }),
                setPerspective: (perspective) => set({
                    perspective,
                    perspectiveSettings: PERSPECTIVE_PRESETS[perspective]
                }),
                updatePerspectiveSettings: (updates) => set((state) => ({
                    perspectiveSettings: { ...state.perspectiveSettings, ...updates }
                })),
                setClipContent: (clipContent) => set({ clipContent }),
                setBorderWidth: (borderWidth) => set({ borderWidth }),
                setBorderColor: (borderColor) => set({ borderColor }),
                setBorderOpacity: (borderOpacity) => set({ borderOpacity }),
                setAnimationIn: (animationIn) => set({ animationIn }),
                setAnimationOut: (animationOut) => set({ animationOut }),

                updateCameraLayer: (updates) => set((state) => {
                    const next = { ...state.cameraLayer, ...updates }
                    const halfW = next.size / 2
                    const isRect = next.mask === 'rectangle'
                    const aspectMultiplier = isRect ? 1 : (16 / 9)
                    const halfH = (next.size / 2) * aspectMultiplier
                    
                    const margin = 2
                    const minX = halfW + margin
                    const maxX = 100 - halfW - margin
                    const minY = halfH + margin
                    const maxY = 100 - halfH - margin
                    
                    next.x = Math.max(minX, Math.min(maxX, next.x))
                    next.y = Math.max(minY, Math.min(maxY, next.y))
                    
                    return { cameraLayer: next }
                }),

                updateCursorSettings: (updates) => set((state) => ({
                    cursorSettings: { ...state.cursorSettings, ...updates }
                })),

                // Zoom Actions
                addZoomRegion: (region) => set((state) => {
                    const SAFE_GAP = 0.05
                    let start = Math.max(0, region.startTime)
                    const dur = Math.max(0.2, region.endTime - region.startTime)

                    const sorted = [...state.zoomRegions].sort((a, b) => a.startTime - b.startTime)
                    for (const z of sorted) {
                        if (start < z.endTime + SAFE_GAP && start + dur > z.startTime - SAFE_GAP) {
                            start = z.endTime + SAFE_GAP
                        }
                    }
                    const safeEnd = start + dur

                    return {
                        zoomRegions: [...state.zoomRegions, { ...region, startTime: start, endTime: safeEnd }].sort((a, b) => a.startTime - b.startTime)
                    }
                }),
                updateZoomRegion: (id, updates) => set((state) => ({
                    zoomRegions: state.zoomRegions.map((r) =>
                        r.id === id ? { ...r, ...updates } : r
                    ).sort((a, b) => a.startTime - b.startTime)
                })),
                removeZoomRegion: (id) => set((state) => ({
                    zoomRegions: state.zoomRegions.filter((r) => r.id !== id)
                })),

                // -- Audio Layer Logic --
                addAudioLayer: (layer) => set((state) => {
                    const SAFE_GAP = 0.05
                    const isSfx = layer.type === 'sfx'
                    let offset = Math.max(0, layer.offset)
                    const dur = Math.max(0.2, layer.duration)

                    const sameTrackClips = state.audioLayers
                        .filter(l => isSfx ? l.type === 'sfx' : l.type !== 'sfx')
                        .sort((a, b) => a.offset - b.offset)

                    for (const a of sameTrackClips) {
                        if (offset < a.offset + a.duration + SAFE_GAP && offset + dur > a.offset - SAFE_GAP) {
                            offset = a.offset + a.duration + SAFE_GAP
                        }
                    }

                    return {
                        audioLayers: [...state.audioLayers, { id: crypto.randomUUID(), trimStart: 0, ...layer, offset }]
                    }
                }),
                updateAudioLayer: (id, updates) => set((state) => ({
                    audioLayers: state.audioLayers.map(l => l.id === id ? { ...l, ...updates } : l)
                })),
                removeAudioLayer: (id) => set((state) => ({
                    audioLayers: state.audioLayers.filter(l => l.id !== id)
                })),

                // -- Text Layer Logic --
                addTextLayer: (layer) => set((state) => {
                    const SAFE_GAP = 0.05
                    let start = Math.max(0, layer.startTime)
                    const dur = Math.max(0.2, layer.endTime - layer.startTime)

                    const sorted = [...state.textLayers].sort((a, b) => a.startTime - b.startTime)
                    for (const t of sorted) {
                        if (start < t.endTime + SAFE_GAP && start + dur > t.startTime - SAFE_GAP) {
                            start = t.endTime + SAFE_GAP
                        }
                    }
                    const safeEnd = start + dur

                    return {
                        textLayers: [...state.textLayers, { ...layer, startTime: start, endTime: safeEnd }]
                    }
                }),
                updateTextLayer: (id, updates) => set((state) => ({
                    textLayers: state.textLayers.map((l) =>
                        l.id === id ? { ...l, ...updates } : l
                    )
                })),
                removeTextLayer: (id) => set((state) => ({
                    textLayers: state.textLayers.filter((l) => l.id !== id),
                    selectedTextLayerId: state.selectedTextLayerId === id ? null : state.selectedTextLayerId
                })),
                // -- Element Layer Logic --
                addElementLayer: (layer) => set((state) => {
                    const SAFE_GAP = 0.05
                    let start = Math.max(0, layer.startTime)
                    const dur = Math.max(0.2, layer.endTime - layer.startTime)

                    const sorted = [...state.elementLayers].sort((a, b) => a.startTime - b.startTime)
                    for (const t of sorted) {
                        if (start < t.endTime + SAFE_GAP && start + dur > t.startTime - SAFE_GAP) {
                            start = t.endTime + SAFE_GAP
                        }
                    }
                    const safeEnd = start + dur

                    return {
                        elementLayers: [...state.elementLayers, { ...layer, startTime: start, endTime: safeEnd }]
                    }
                }),
                updateElementLayer: (id, updates) => set((state) => ({
                    elementLayers: state.elementLayers.map((l) =>
                        l.id === id ? { ...l, ...updates } : l
                    )
                })),
                removeElementLayer: (id) => set((state) => ({
                    elementLayers: state.elementLayers.filter((l) => l.id !== id),
                    selectedElementLayerId: state.selectedElementLayerId === id ? null : state.selectedElementLayerId
                })),
                duplicateElementLayer: (id) => set((state) => {
                    const layer = state.elementLayers.find(l => l.id === id)
                    if (!layer) return {}
                    const dur = layer.endTime - layer.startTime
                    const newLayer = {
                        ...layer,
                        id: crypto.randomUUID(),
                        startTime: layer.endTime + 0.1,
                        endTime: layer.endTime + 0.1 + dur
                    }
                    return {
                        elementLayers: [...state.elementLayers, newLayer],
                        selectedElementLayerId: newLayer.id,
                        selectedClipId: null,
                        selectedTextLayerId: null,
                        selectedAudioLayerId: null,
                        selectedZoomRegionId: null,
                        selectedCameraLayerSelected: false
                    }
                }),


                // -- Clip Logic --
                initClips: (duration, hasCamera) => set((state) => {
                    if (state.clips.length > 0) return {} // Already initialized
                    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0
                    return {
                        sourceDuration: safeDuration,
                        clips: [{ id: crypto.randomUUID(), start: 0, end: safeDuration, offset: 0, status: 'visible' }],
                        cameraLayer: { ...state.cameraLayer, visible: !!hasCamera }
                    }
                }),

                 splitClip: (time) => set((state) => {
                    const speed = (c: Clip) => c.speed ?? 1
                    const clipDur = (c: Clip) => (c.end - c.start) / speed(c)
                    let targetClip = state.clips.find(c => c.id === state.selectedClipId && time >= c.offset && time < c.offset + clipDur(c) && c.status === 'visible')
                    if (!targetClip) {
                        targetClip = state.clips.find(c => time >= c.offset && time < c.offset + clipDur(c) && c.status === 'visible')
                    }
                    if (!targetClip) return {}

                    const relativeTime = time - targetClip.offset // Time into the timeline clip
                    const splitPointInSource = targetClip.start + relativeTime * speed(targetClip)

                    const clip1: Clip = {
                        ...targetClip,
                        end: splitPointInSource
                    }
                    const clip2: Clip = {
                        id: crypto.randomUUID(),
                        start: splitPointInSource,
                        end: targetClip.end,
                        offset: time,
                        speed: targetClip.speed,
                        status: 'visible'
                    }

                    // Replace target with two new clips
                    const index = state.clips.indexOf(targetClip)
                    const newClips = [...state.clips]
                    newClips.splice(index, 1, clip1, clip2)

                    return {
                        clips: newClips,
                        selectedClipId: clip2.id,
                        selectedTextLayerId: null,
                    selectedElementLayerId: null,
                        selectedAudioLayerId: null,
                        selectedZoomRegionId: null,
                        selectedCameraLayerSelected: false
                    }
                }),

                deleteClip: (id) => set((state) => {
                    const newClips = state.clips.map(c => c.id === id ? { ...c, status: 'deleted' as const } : c)

                    // Recalculate duration (speed-aware)
                    const visibleClips = newClips.filter(c => c.status === 'visible')
                    const newDuration = visibleClips.length > 0
                        ? visibleClips.reduce((max, c) => Math.max(max, c.offset + (c.end - c.start) / (c.speed ?? 1)), 0)
                        : 0

                    return {
                        clips: newClips,
                        duration: newDuration > 0.1 ? newDuration : state.duration
                    }
                }),

                updateClip: (id, updates) => set((state) => {
                    const newClips = state.clips.map(c => c.id === id ? { ...c, ...updates } : c)

                    // Recalculate duration (speed-aware)
                    const visibleClips = newClips.filter(c => c.status === 'visible')
                    const newDuration = visibleClips.length > 0
                        ? visibleClips.reduce((max, c) => Math.max(max, c.offset + (c.end - c.start) / (c.speed ?? 1)), 0)
                        : state.duration

                    return { clips: newClips, duration: newDuration }
                }),

                removeGap: (atTime) => set((state) => {
                    const sorted = [...state.clips]
                        .filter(c => c.status === 'visible')
                        .sort((a, b) => a.offset - b.offset)

                    let gapSize = 0
                    let shiftThreshold = 0

                    // Check gap before first clip
                    if (sorted.length > 0 && atTime < sorted[0].offset) {
                        gapSize = sorted[0].offset
                        shiftThreshold = sorted[0].offset
                    } else {
                        for (let i = 0; i < sorted.length - 1; i++) {
                            const currentEnd = sorted[i].offset + (sorted[i].end - sorted[i].start) / (sorted[i].speed ?? 1)
                            const nextStart = sorted[i + 1].offset
                            if (atTime >= currentEnd - 0.1 && atTime <= nextStart + 0.1) {
                                gapSize = nextStart - currentEnd
                                shiftThreshold = nextStart
                                break
                            }
                        }
                    }

                    if (gapSize > 0.01) { // Min gap
                        const updated = state.clips.map(c => {
                            if (c.status === 'visible' && c.offset >= shiftThreshold) {
                                return { ...c, offset: Math.max(0, c.offset - gapSize) }
                            }
                            return c
                        })

                        const newDuration = updated
                            .filter(c => c.status === 'visible')
                            .reduce((max, c) => Math.max(max, c.offset + (c.end - c.start) / (c.speed ?? 1)), 0)

                        return { clips: updated, duration: newDuration }
                    }
                    return {}
                }),

                // -- Auto Zoom Logic --
                generateAutoZoom: (events, screenWidth, screenHeight) => set((state) => {
                    const videoDurationMs = state.duration > 0 ? state.duration * 1000 : undefined
                    const zoomRegions = generateZoomSuggestions(events, screenWidth, screenHeight, videoDurationMs)
                    return { zoomRegions }
                }),

                // -- Playback --
                setCurrentTime: (currentTime) => set({ currentTime }),
                setDuration: (duration) => set({
                    duration: Number.isFinite(duration) && duration > 0 ? duration : 0
                }),
            }),
            {
                name: 'screenrecord-editor-storage',
                partialize: (state) => ({
                    // Persist everything except playback state
                    backgroundType: state.backgroundType,
                    backgroundValue: state.backgroundValue,
                    backgroundBlur: state.backgroundBlur,
                    backgroundDim: state.backgroundDim,
                    backgroundAttribution: state.backgroundAttribution,
                    padding: state.padding,
                    borderRadius: state.borderRadius,
                    shadow: state.shadow,
                    aspectRatio: state.aspectRatio,
                    perspective: state.perspective,
                    borderWidth: state.borderWidth,
                    borderColor: state.borderColor,
                    animationIn: state.animationIn,
                    animationOut: state.animationOut,
                    zoomRegions: state.zoomRegions,
                    clips: state.clips,
                    videoCrop: state.videoCrop,
                    sourceDuration: state.sourceDuration,
                    textLayers: state.textLayers,
                    elementLayers: state.elementLayers,
                    cameraLayer: state.cameraLayer, // Persist camera layer
                    cursorSettings: state.cursorSettings, // Persist cursor settings
                    perspectiveSettings: state.perspectiveSettings,
                    clipContent: state.clipContent,
                    trackVisibility: state.trackVisibility,
                }),
            }
        ),
        {
            // Zundo Configuration
            limit: 100,
            partialize: (state) => {
                const {
                    currentTime,
                    duration,
                    isPlaying,
                    selectedClipId,
                    selectedTextLayerId,
                    selectedAudioLayerId,
                    selectedZoomRegionId,
                    selectedCameraLayerSelected,
                    clipboardItem,
                    ...rest
                } = state
                return rest // Don't track time updates or selections in history
            },
            equality: (past, present) => {
                // simple deep check or specialized one for perf
                return JSON.stringify(past) === JSON.stringify(present)
            }
        }
    )
)
